<template>
  <AuthHeader
    :title="props.isReset ? t('Восстановление пароля') : t('Введите код')"
    :subtitle="
      props.method === 'email'
        ? t('На вашу эл. почту отправлен код подтверждения, введите его ниже для подтверждения личности')
        : t('На ваш телефон отправлен код подтверждения, введите его ниже для подтверждения личности')
    "
  />
  <BaseForm
    v-model="formModel"
    :schema="[
      {
        type: 'code',
        name: 'code',
        label: t('Код подтверждения'),
        attrs: { 'length': 6, 'data-testid': 'auth-code-input' },
        required: true,
      },
    ]"
    :when-handle-submit="sendCode"
  >
    <template #actions="{ nextTabindex }">
      <div class="auth-verify-form__actions">
        <BasePseudoLink
          :variant="methodExpiresIn ? 'secondary' : 'primary'"
          :cursor="methodExpiresIn ? 'not-allowed' : 'pointer'"
          data-testid="auth-repeat-code-link"
          @click.prevent="handleGetNewCode"
        >
          {{ t('Отправить код повторно') }}
          <ClientOnly v-if="methodExpiresIn">
            ( {{ `${t('через')} ${timeString}` }} )
          </ClientOnly>
        </BasePseudoLink>
        <BaseButton
          with-full
          with-decor
          data-testid="auth-prev-btn"
          @click="emit('back')"
        >
          {{ t('Назад') }}
        </BaseButton>
        <BaseButton
          type="submit"
          with-decor
          with-full
          variant="primary"
          data-testid="auth-next-btn"
          :tabindex="nextTabindex"
        >
          {{ t('Далее') }}
        </BaseButton>
      </div>
    </template>
  </BaseForm>
  <AuthContactDevelopersBlock class="auth-verify-form__developers" />
</template>

<script setup lang="ts">
import type { AuthMethod } from '~/entities/auth'

import { useI18n } from 'vue-i18n'

import { AuthContactDevelopersBlock, AuthHeader, METHOD_EXPIRES_IN, useAccountStore } from '~/entities/auth'
import { VerificationTypes } from '~/shared/api'
import { BaseButton, BaseForm, BasePseudoLink } from '~/shared/ui'
import i18n from '../model/AuthVerifyForm.i18n.json'

interface Props {
  isReset: boolean
  method: AuthMethod
}

const props = defineProps<Props>()
const emit = defineEmits<{
  (e: 'success', params: { nextStep: 'profile' | 'set-password', method?: AuthMethod, isReset?: boolean }): void
  (e: 'back'): void
}>()

const { t } = useI18n(i18n)
const accountStore = useAccountStore()
const { methodExpiresIn } = storeToRefs(accountStore)
const {
  requestRegistrationOtp,
  requestPasswordResetOtp,
  verifyRegistrationOtp,
  verifyPasswordResetOtp,
} = accountStore

const formModel = ref({ code: '' })
const countDown = ref(METHOD_EXPIRES_IN)
const interval = ref<ReturnType<typeof setInterval>>()

const timeString = computed(() => {
  const minutes = Math.floor(countDown.value / 60)
  const seconds = Math.floor(countDown.value % 60)
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
})

function requestOtp() {
  if (props.isReset) {
    requestPasswordResetOtp()
  }
  else {
    requestRegistrationOtp(
      props.method === 'email' ? VerificationTypes.Email : VerificationTypes.Phone,
    )
  }
}

function startCountDown() {
  interval.value = setInterval(() => {
    countDown.value--
    if (countDown.value <= 0) {
      clearInterval(interval.value!)
      countDown.value = METHOD_EXPIRES_IN
    }
  }, 1000)
}

function handleGetNewCode() {
  if (!methodExpiresIn.value) {
    clearInterval(interval.value)
    countDown.value = METHOD_EXPIRES_IN
    startCountDown()
  }
  requestOtp()
}

async function sendCode() {
  if (!props.isReset) {
    try {
      await verifyRegistrationOtp(
        formModel.value.code,
        props.method === 'email' ? VerificationTypes.Email : VerificationTypes.Phone,
      )
      emit('success', { nextStep: 'profile', method: props.method })
    }
    catch {
      formModel.value.code = ''
    }
    return
  }

  try {
    await verifyPasswordResetOtp(formModel.value.code)
    emit('success', { nextStep: 'set-password', method: props.method, isReset: true })
  }
  catch {
    formModel.value.code = ''
  }
}

onMounted(() => {
  requestOtp()
  startCountDown()
})

onBeforeUnmount(() => {
  clearInterval(interval.value)
})
</script>

<style scoped lang="scss">
.auth-verify-form__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  width: 100%;

  > * {
    flex: 1 1 40%;
  }

  :deep(.base-pseudo-link) {
    flex: 1 1 100%;
    text-decoration: underline;
  }
}

.auth-verify-form__developers {
  margin-top: rem(21px);
}
</style>
